import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearState, resetPasswordFunc } from "../../redux/Slices/authSlice";
import { toast } from "react-toastify";

const ResetPassword = () => {
  const { t } = useTranslation("global");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [resetInputs, setResetInputs] = useState({
    email: "",
    new_password: "",
    new_password_confirmation: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const verified = localStorage.getItem("verified");
    if (!verified) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setResetInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!resetInputs.email) {
      toast.error(t("sign.fillFields"));
      return;
    }

    if (resetInputs.new_password.length < 8) {
      toast.error(t("password_must_be_at_least_8_chars"));
      return;
    }

    if (resetInputs.new_password !== resetInputs.new_password_confirmation) {
      toast.error(t("passwords_do_not_match"));
      return;
    }

    setIsLoading(true);
    dispatch(resetPasswordFunc(resetInputs))
      .unwrap()
      .then(() => {
        toast.success(t("password_changed_successfully"));
        setTimeout(() => {
          localStorage.removeItem("verified");
          navigate("/login", { replace: true });
          dispatch(clearState());
        }, 1500);
      })
      .catch((err) => {
        toast.error(t("failed_to_reset_password") || err?.message);
        dispatch(clearState());
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <main className="d-flex justify-content-center align-items-center min-vh-100">
      <div className="container">
        <form
          className="table_form form-style my-3 py-3"
          onSubmit={handleSubmit}
        >
          <div className="form-div">
            <div className="text-center my-3">
              <Link to="/">
                <picture>
                  <source srcSet="/img/fav.webp" type="image/webp" />
                  <img
                    src="/img/fav.png"
                    alt="Tripway"
                    width={180}
                    height={60}
                    decoding="async"
                    style={{ width: "auto", maxHeight: "60px", height: "60px" }}
                  />
                </picture>
              </Link>
            </div>

            <h1 className="h5 text-center text-md fw-bold main-color mb-4">
              <i className="bi bi-key-fill text-lg mx-2"></i>
              {t("reset_password")}
            </h1>

            <div className="mb-3">
              <label className="form-label fw-semibold mb-2 d-flex align-items-center">
                <i className="bi bi-envelope mx-2" style={{ color: "var(--green-color)" }}></i>
                {t("sign.email")}
              </label>
              <input
                type="email"
                name="email"
                className="form-control"
                placeholder="example@gmail.com"
                value={resetInputs.email}
                onChange={handleChange}
                required
                style={{
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "0.375rem",
                }}
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold mb-2 d-flex align-items-center">
                <i className="bi bi-lock-fill mx-2" style={{ color: "var(--green-color)" }}></i>
                {t("new_password")}
              </label>
              <div className="position-relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="new_password"
                  className="form-control"
                  placeholder={t("new_password")}
                  value={resetInputs.new_password}
                  onChange={handleChange}
                  required
                  style={{
                    padding: "0.75rem",
                    paddingRight: "2.5rem",
                    fontSize: "1rem",
                    border: "1px solid #dee2e6",
                    borderRadius: "0.375rem",
                  }}
                />
                <button
                  type="button"
                  className="btn btn-link position-absolute"
                  style={{
                    top: "50%",
                    right: "0.5rem",
                    transform: "translateY(-50%)",
                    padding: "0.25rem",
                    color: "#6c757d",
                    textDecoration: "none",
                    border: "none",
                    background: "none",
                  }}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                </button>
              </div>
              <small className="text-muted d-block mt-1">
                {t("password_must_be_at_least_8_chars")}
              </small>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold mb-2 d-flex align-items-center">
                <i className="bi bi-lock-fill mx-2" style={{ color: "var(--green-color)" }}></i>
                {t("confirm_new_password")}
              </label>
              <div className="position-relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="new_password_confirmation"
                  className="form-control"
                  placeholder={t("confirm_new_password")}
                  value={resetInputs.new_password_confirmation}
                  onChange={handleChange}
                  required
                  style={{
                    padding: "0.75rem",
                    paddingRight: "2.5rem",
                    fontSize: "1rem",
                    border: "1px solid #dee2e6",
                    borderRadius: "0.375rem",
                  }}
                />
                <button
                  type="button"
                  className="btn btn-link position-absolute"
                  style={{
                    top: "50%",
                    right: "0.5rem",
                    transform: "translateY(-50%)",
                    padding: "0.25rem",
                    color: "#6c757d",
                    textDecoration: "none",
                    border: "none",
                    background: "none",
                  }}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <i className={`bi ${showConfirmPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn d-block w-100 mb-3"
              disabled={isLoading}
              style={{
                backgroundColor: "var(--green-color)",
                color: "white",
                padding: "0.75rem",
                fontSize: "1rem",
                fontWeight: "600",
                borderRadius: "0.375rem",
              }}
            >
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {t("sign.loading")}
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  {t("btns.send")}
                </>
              )}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className="text-decoration-none"
                style={{ color: "var(--green-color)" }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                {t("back_to_login") || "Back to Login"}
              </Link>
            </div>
          </div>
        </form>
      </div>
    </main>
  );
};

export default ResetPassword;
